import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ArrowRight, Check, UtensilsCrossed, ShoppingBag, CalendarCheck, MessageCircle, Gift, Truck, CreditCard,
  Languages, BarChart3, Users, QrCode, ChefHat, Package, Clock, Plus, ChevronDown, Sparkles,
  MonitorSmartphone, Boxes, UserRound, Paintbrush, Bell, Printer, BedDouble, Briefcase, Star,
} from 'lucide-react'
import SiteLayout from './SiteLayout'
import PricingSection from './PricingSection'
import { t } from '../../i18n'
import { SITE } from '../../config/site'
import dashboardShot from '../../assets/landing-dashboard.webp'

const FAQ = [
  ['How long does it take to go live?', 'Most businesses publish their website the same day. Add your logo, a few products or services, and share your link - the setup guide walks you through it.'],
  ['Do I need technical skills?', 'No. Everything is done from a simple dashboard in Arabic or English. If you get stuck, our team in Muscat can help.'],
  ['Which payment methods can my customers use?', 'Cash on delivery or pickup, bank transfer with receipt upload, and card payments through your own Stripe account.'],
  ['Can I set different delivery prices per city?', 'Yes. Add the main cities of any Gulf country in one click (Oman, Saudi Arabia, the UAE, Qatar, Kuwait or Bahrain), or type your own areas, and give each one its own price and free-delivery amount.'],
  ['Can one store sell products and take bookings too?', 'Yes. Depending on your plan you can run the menu, an online store and bookings together in the same store, managed from one dashboard.'],
  ['Do you take a commission on my sales?', 'No. You pay a fixed monthly plan; every sale is yours.'],
  ['Will my website have its own address?', 'Yes. Every business gets its own subdomain the moment it signs up, plus QR codes for tables, flyers and packaging.'],
]

function BrowserFrame({ url, children, className = '' }) {
  return (
    <div className={`overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-900/10 ring-1 ring-slate-900/10 ${className}`}>
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
        <span className="flex gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-slate-300" /><i className="h-2.5 w-2.5 rounded-full bg-slate-300" /><i className="h-2.5 w-2.5 rounded-full bg-slate-300" /></span>
        <span className="mx-auto truncate rounded-md bg-white px-3 py-0.5 text-xs text-slate-500 ring-1 ring-slate-200" dir="ltr">{url}</span>
      </div>
      {children}
    </div>
  )
}

// A real store dashboard, with the live order cards floating over it.
function HeroVisual() {
  return (
    <div className="relative mx-auto w-full max-w-2xl">
      <div className="absolute -inset-10 -z-10 rounded-[3rem] bg-gradient-to-tr from-blue-100 via-sky-50 to-amber-50 blur-2xl" aria-hidden="true" />
      <BrowserFrame url={`yourstore.${SITE.domain}/admin/dashboard`}>
        <img src={dashboardShot} width="1600" height="779" alt={t("A store dashboard with today's sales, orders and best-selling products")}
          className="block h-auto w-full" loading="eager" />
      </BrowserFrame>
      <div className="absolute -bottom-6 -start-4 w-60 rounded-2xl bg-white p-4 shadow-xl ring-1 ring-slate-900/5 sm:-start-10">
        <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500" />{t('New order')} #1043</div>
        <p className="mt-1 text-sm font-semibold text-slate-900">{t('Delivery to Al Khuwair')}</p>
        <p className="text-xs text-slate-500">{t('2 items · 4.800 OMR')}</p>
      </div>
      <div className="absolute -top-5 -end-3 rounded-2xl bg-white px-4 py-3 shadow-xl ring-1 ring-slate-900/5 sm:-end-8">
        <p className="text-xs text-slate-500">{t("Today's orders")}</p>
        <p className="text-2xl font-extrabold text-slate-900">24 <span className="text-xs font-semibold text-emerald-600">+18%</span></p>
      </div>
    </div>
  )
}

function KitchenMock() {
  const tickets = [['Table 4', ['2× Lamb shuwa', '1× Karak tea'], 'bg-amber-100 text-amber-800', 'Preparing'], ['Delivery · Bawshar', ['1× Machboos', '2× Lemon mint'], 'bg-sky-100 text-sky-800', 'New'], ['Pickup', ['3× Shawarma'], 'bg-emerald-100 text-emerald-800', 'Ready']]
  return (
    <BrowserFrame url={`yourrestaurant.${SITE.domain}/admin/kitchen`}>
      <div className="grid gap-3 bg-slate-50 p-4 sm:grid-cols-3">
        {tickets.map(([title, lines, chip, status]) => (
          <div key={title} className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
            <div className="flex items-center justify-between gap-2"><p className="truncate text-sm font-bold">{t(title)}</p><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${chip}`}>{t(status)}</span></div>
            <ul className="mt-2 space-y-1 text-xs text-slate-600">{lines.map(l => <li key={l}>{t(l)}</li>)}</ul>
          </div>
        ))}
      </div>
    </BrowserFrame>
  )
}

function ShippingMock() {
  const rows = [['Muscat', 'Same day', '1.500 OMR'], ['Salalah', '2-3 days', '3.000 OMR'], ['Dubai', '2-3 days', '35 AED'], ['Riyadh', '3-4 days', '25 SAR'], ['Doha', '3-4 days', '30 QAR']]
  return (
    <BrowserFrame url={`yourstore.${SITE.domain}/admin/shipping`}>
      <div className="p-4">
        <p className="text-sm font-bold">{t('Delivery areas')}</p>
        <ul className="mt-3 divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200">
          {rows.map(([city, eta, p]) => (
            <li key={city} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
              <span><span className="font-medium">{t(city)}</span> <span className="text-xs text-slate-500">· {t(eta)}</span></span>
              <span className="rounded-lg bg-slate-50 px-2 py-1 font-semibold tabular-nums ring-1 ring-slate-200" dir="ltr">{p}</span>
            </li>
          ))}
        </ul>
      </div>
    </BrowserFrame>
  )
}

function BookingMock() {
  const slots = ['9:00', '9:30', '10:00', '10:30', '11:00', '11:30', '16:00', '16:30']
  return (
    <BrowserFrame url={`yoursalon.${SITE.domain}`}>
      <div className="p-4">
        <p className="text-sm font-bold">{t('Hair cut & styling')} · 45 {t('min')}</p>
        <p className="text-xs text-slate-500">{t('Choose a time on Sunday')}</p>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {slots.map((s, i) => (
            <span key={s} className={`rounded-lg py-2 text-center text-xs font-semibold ring-1 ${i === 2 ? 'bg-blue-700 text-white ring-blue-700' : i === 5 ? 'bg-slate-100 text-slate-400 line-through ring-slate-100' : 'bg-white text-slate-700 ring-slate-200'}`} dir="ltr">{s}</span>
          ))}
        </div>
        <div className="mt-4 rounded-xl bg-blue-700 py-2.5 text-center text-sm font-semibold text-white">{t('Confirm booking')}</div>
      </div>
    </BrowserFrame>
  )
}

function HotelMock() {
  const rooms = [['Deluxe room', '35 OMR', 'Free'], ['Family suite', '60 OMR', 'Booked'], ['Sea view room', '45 OMR', 'Free']]
  return (
    <BrowserFrame url={`yourhotel.${SITE.domain}`}>
      <div className="p-4">
        <p className="text-sm font-bold">{t('Choose your room')}</p>
        <p className="text-xs text-slate-500">{t('Check-in on Thursday')}</p>
        <ul className="mt-3 space-y-2">
          {rooms.map(([room, price, state]) => (
            <li key={room} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm ring-1 ring-slate-200">
              <span className="font-medium">{t(room)}</span>
              <span className="flex items-center gap-2">
                <span className="font-semibold tabular-nums" dir="ltr">{price}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${state === 'Free' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>{t(state)}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </BrowserFrame>
  )
}

function OfficeMock() {
  const hours = ['9:00', '10:00', '11:00', '12:00', '13:00', '14:00']
  const rooms = [['Meeting room A', [1, 2]], ['Hot desk', [0, 4]], ['Private office', [3]]]
  return (
    <BrowserFrame url={`yourspace.${SITE.domain}`}>
      <div className="overflow-x-auto p-4">
        <p className="mb-3 text-sm font-bold">{t('Free and booked hours today')}</p>
        <div className="grid min-w-[22rem] grid-cols-[7rem_repeat(6,1fr)] gap-1 text-[11px]">
          <span />
          {hours.map(h => <span key={h} className="text-center text-slate-500" dir="ltr">{h}</span>)}
          {rooms.map(([room, busy]) => [
            <span key={room} className="truncate py-1.5 font-medium">{t(room)}</span>,
            ...hours.map((h, i) => <span key={room + h} className={`rounded ${busy.includes(i) ? 'bg-blue-600' : 'bg-slate-100'}`} />),
          ])}
        </div>
      </div>
    </BrowserFrame>
  )
}

function LoyaltyMock() {
  return (
    <BrowserFrame url={`yourstore.${SITE.domain}/account`}>
      <div className="p-4">
        <div className="rounded-xl bg-gradient-to-br from-blue-700 to-blue-900 p-4 text-white">
          <p className="text-xs text-blue-100">{t('Your points')}</p>
          <p className="text-3xl font-extrabold tabular-nums" dir="ltr">1,250</p>
          <div className="mt-3 h-1.5 rounded-full bg-white/20"><div className="h-1.5 w-3/4 rounded-full bg-amber-300" /></div>
          <p className="mt-1.5 text-xs text-blue-100">{t('250 more points for a free coffee')}</p>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-emerald-50 p-3 text-xs text-emerald-900 ring-1 ring-emerald-100">
          <MessageCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('Weekend offer: 20% off all desserts. Show this message at the counter.')}</span>
        </div>
      </div>
    </BrowserFrame>
  )
}

const APP_SECTIONS = [
  {
    id: 'restaurants', icon: UtensilsCrossed, label: 'For restaurants & cafés', title: 'From the table QR code to the kitchen screen',
    text: 'Guests scan, order and pay from their phone. Orders reach the kitchen display instantly, and your cashier rings up walk-ins on the POS.',
    points: ['QR menu for every table', 'POS and kitchen display', 'Delivery and pickup orders', 'Table reservations', 'Recipes and ingredient stock'],
    visual: <KitchenMock />,
  },
  {
    id: 'stores', icon: ShoppingBag, label: 'For online stores', title: 'A shop your customers enjoy buying from',
    text: 'Show your products with photos and options, keep stock up to date with every sale, and charge the right delivery price for every city.',
    points: ['Product galleries and variants', 'Stock that updates itself', 'Delivery prices for every city in the Gulf', 'Card, bank transfer or cash on delivery', 'Low-stock email alerts'],
    visual: <ShippingMock />,
  },
  {
    id: 'bookings', icon: CalendarCheck, label: 'For salons, clinics & services', title: 'Customers book a free slot by themselves',
    text: 'Publish your services and opening hours. Customers pick a time that is really free, and you see the day ahead at a glance.',
    points: ['Live availability calendar', 'Services with price and duration', 'Opening hours and days off', 'Email confirmations'],
    visual: <BookingMock />,
  },
  {
    id: 'hotels', icon: BedDouble, label: 'For hotels & guest houses', title: 'Take room bookings without the middleman',
    text: 'List your rooms with photos and prices. Guests pick a free time and book directly on your own website - no commission on any booking.',
    points: ['Rooms with photos and prices', 'Live availability calendar', 'Booking confirmations by email', 'Pay at the hotel, by bank transfer or card'],
    visual: <HotelMock />,
  },
  {
    id: 'offices', icon: Briefcase, label: 'For offices & coworking spaces', title: 'Rent desks and meeting rooms by the hour',
    text: 'List your desks, meeting rooms and private offices. Members book the free hours themselves, and a taken hour cannot be booked twice.',
    points: ['Desks, meeting rooms and offices', 'Hourly time slots', 'Only free times can be booked', 'Opening hours and days off'],
    visual: <OfficeMock />,
  },
  {
    id: 'loyalty', icon: Star, label: 'For every business', title: 'Bring your customers back again and again',
    text: 'Customers collect points on every order and swap them for rewards. Send offers on WhatsApp to the customers who asked for them.',
    points: ['Loyalty points in every plan', 'Rewards your customers can claim', 'Customer accounts with order history', 'WhatsApp offers from your own number'],
    visual: <LoyaltyMock />,
  },
]

// Every app a store gets, beyond the three services.
const TOOLS = [
  { icon: MonitorSmartphone, title: 'POS & cashier', desc: 'Ring up walk-in sales, take cash or card, print the bill and see every order in one list.' },
  { icon: ChefHat, title: 'Kitchen screen', desc: 'New orders appear on the kitchen screen with a sound, from the website, the tables and the POS.' },
  { icon: Gift, title: 'Loyalty points', desc: 'Customers earn points on every order and swap them for rewards. Included in every plan.' },
  { icon: MessageCircle, title: 'WhatsApp marketing', desc: 'Send offers from your own number to customers who agreed to hear from you.' },
  { icon: Users, title: 'Staff & roles', desc: 'Cashier, kitchen, driver, accountant or your own roles, each seeing only their pages.' },
  { icon: Boxes, title: 'Inventory & stock', desc: 'Stock that updates with every sale, ingredient recipes and low-stock alerts.' },
  { icon: Truck, title: 'Delivery & shipping', desc: 'A price for every city in the Gulf, free delivery from an amount you choose, or pickup.' },
  { icon: BarChart3, title: 'Reports & accounting', desc: 'Sales by day, best sellers, payment methods and staff performance, ready to export.' },
  { icon: QrCode, title: 'QR codes & tables', desc: 'Printable QR codes for every table, flyer and package, linked to your own website.' },
  { icon: CreditCard, title: 'Online payments', desc: 'Cash, bank transfer with receipt upload, or cards with your own Stripe account.' },
  { icon: UserRound, title: 'Customer accounts', desc: 'Customers follow their orders, bookings and points from their own account.' },
  { icon: Paintbrush, title: 'Website editor', desc: 'Pick a template, then change texts, colors and photos and see your website live.' },
  { icon: Bell, title: 'New order alerts', desc: 'A sound and a message on your dashboard the moment an order or booking comes in.' },
  { icon: Printer, title: 'Printed bills', desc: 'Print a clear receipt from the POS or any order, with your logo and your currency.' },
  { icon: Languages, title: 'Arabic & English', desc: 'Your website and dashboard in both languages, right-to-left included.' },
]

const GULF = ['Oman', 'Saudi Arabia', 'United Arab Emirates', 'Qatar', 'Kuwait', 'Bahrain']

const STEPS = [
  { icon: Sparkles, title: 'Create your account', desc: 'Pick your plan and your services, then choose your website name. No card needed.' },
  { icon: Package, title: 'Add your logo and products', desc: 'The setup guide shows you what to do next: logo, first category, first product.' },
  { icon: ChefHat, title: 'Share your link and sell', desc: 'Send your link on WhatsApp and Instagram, print your QR codes and take your first order.' },
]

export default function LandingPage() {
  const location = useLocation()
  // Arriving from another page with /#stores: scroll once the section exists.
  useEffect(() => {
    if (!location.hash) return
    const el = document.getElementById(location.hash.slice(1))
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 50)
  }, [location.hash])

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl items-center gap-16 px-4 pb-24 pt-12 sm:px-6 sm:pt-20 lg:grid-cols-[1.05fr_1fr]">
          <div>
            <h1 className="text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-6xl">
              {t('Your website, orders and customers.')} <span className="text-blue-700">{t('All in one place.')}</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-8 text-slate-600">
              {t('Launch a website for your restaurant, shop or salon in minutes. Take orders and bookings, keep your customers coming back, deliver anywhere in the Gulf and get paid - in Arabic and English.')}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/get-started" className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-6 py-3.5 font-semibold text-white shadow-lg shadow-blue-700/20 hover:bg-blue-800">
                {t('Start free')} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
              <a href="#apps" className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-6 py-3.5 font-semibold text-slate-800 hover:bg-slate-50">{t('See what you can do')}</a>
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              {['No card needed', 'Live in minutes', 'Made for the Gulf'].map(x => <li key={x} className="flex items-center gap-1.5"><Check className="h-4 w-4 text-blue-700" />{t(x)}</li>)}
            </ul>
          </div>
          <HeroVisual />
        </div>
      </section>

      {/* Numbers */}
      <section className="border-y border-slate-100 bg-slate-50/70">
        <dl className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-4 py-10 text-center sm:px-6 md:grid-cols-4">
          {[['5', 'business types in one platform'], ['15+', 'apps included'], ['6', 'Gulf countries'], ['0%', 'commission on your sales']].map(([n, l]) => (
            <div key={l}><dt className="text-3xl font-extrabold text-slate-900">{n}</dt><dd className="mt-1 text-sm text-slate-600">{t(l)}</dd></div>
          ))}
        </dl>
      </section>

      {/* Apps */}
      <section id="apps" className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-semibold text-blue-700">{t('One account, the right tools')}</p>
          <h2 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">{t('Made for the way your business works')}</h2>
          <p className="mt-3 text-lg text-slate-600">{t('Run one service, or combine the menu, an online store and bookings in the same store - one dashboard for all of it.')}</p>
          <nav className="mt-8 flex flex-wrap justify-center gap-2" aria-label={t('Business types')}>
            {APP_SECTIONS.map(app => (
              <a key={app.id} href={`#${app.id}`} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-blue-300 hover:text-blue-700">
                <app.icon className="h-4 w-4" />{t(app.label)}
              </a>
            ))}
          </nav>
        </div>
        <div className="mt-16 space-y-24">
          {APP_SECTIONS.map((app, i) => (
            <div key={app.id} id={app.id} className="grid scroll-mt-24 items-center gap-10 lg:grid-cols-2 lg:gap-16">
              <div className={i % 2 ? 'lg:order-2' : ''}>
                <p className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700"><app.icon className="h-5 w-5" />{t(app.label)}</p>
                <h3 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">{t(app.title)}</h3>
                <p className="mt-3 text-lg leading-8 text-slate-600">{t(app.text)}</p>
                <ul className="mt-6 space-y-2.5">
                  {app.points.map(p => <li key={p} className="flex gap-2 text-slate-700"><Check className="mt-1 h-4 w-4 shrink-0 text-blue-700" />{t(p)}</li>)}
                </ul>
                <Link to="/get-started" className="mt-7 inline-flex items-center gap-2 font-semibold text-blue-700 hover:text-blue-800">{t('Start with this')} <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
              </div>
              <div className={i % 2 ? 'lg:order-1' : ''}>{app.visual}</div>
            </div>
          ))}
        </div>
      </section>

      {/* All the apps */}
      <section id="tools" className="scroll-mt-20 bg-slate-900 text-white">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold text-blue-300">{t('All the apps your business needs')}</p>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">{t('Everything you need to sell, nothing to install')}</h2>
            <p className="mt-3 text-lg text-slate-300">{t('Every store comes with these tools, ready to switch on from your dashboard.')}</p>
          </div>
          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {TOOLS.map(f => (
              <div key={f.title} className="bg-slate-900 p-6">
                <div className="flex items-center justify-between gap-3">
                  <f.icon className="h-6 w-6 text-blue-300" />
                  {f.tag && <span className="rounded-full bg-amber-400/15 px-2.5 py-0.5 text-xs font-semibold text-amber-300">{t(f.tag)}</span>}
                </div>
                <h3 className="mt-4 font-semibold">{t(f.title)}</h3>
                <p className="mt-1.5 text-sm leading-6 text-slate-400">{t(f.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gulf */}
      <section className="border-b border-slate-100">
        <div className="mx-auto max-w-6xl px-4 py-14 text-center sm:px-6">
          <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{t('Built for businesses across the Gulf')}</h2>
          <p className="mx-auto mt-3 max-w-2xl text-slate-600">{t('Local currencies, delivery to every city and Arabic first - wherever you sell.')}</p>
          <ul className="mt-8 flex flex-wrap justify-center gap-3">
            {GULF.map(country => <li key={country} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">{t(country)}</li>)}
          </ul>
        </div>
      </section>

      {/* Steps */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <p className="text-sm font-semibold text-blue-700">{t('Getting started')}</p>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">{t('Three steps to your first order')}</h2>
            <p className="mt-3 text-lg text-slate-600">{t('A short setup guide on your dashboard tells you exactly what to do next.')}</p>
            <Link to="/get-started" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 font-semibold text-white hover:bg-slate-800">{t('Create my website')} <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
          </div>
          <ol className="space-y-4">
            {STEPS.map((s, i) => (
              <li key={s.title} className="flex gap-5 rounded-2xl border border-slate-200 p-6">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-blue-50 font-bold text-blue-700">0{i + 1}</span>
                <div><h3 className="font-bold">{t(s.title)}</h3><p className="mt-1 text-slate-600">{t(s.desc)}</p></div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <div className="border-t border-slate-100"><PricingSection /></div>

      {/* FAQ */}
      <section className="border-t border-slate-100 bg-slate-50/70">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_1.6fr]">
          <div>
            <p className="text-sm font-semibold text-blue-700">{t('Questions')}</p>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight">{t('Good to know before you start')}</h2>
            <p className="mt-3 text-slate-600">{t('Something else on your mind?')} <Link to="/contact" className="font-semibold text-blue-700 underline-offset-4 hover:underline">{t('Talk to us')}</Link></p>
          </div>
          <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {FAQ.map(([q, a]) => (
              <details key={q} className="group p-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-slate-900">
                  {t(q)}<ChevronDown className="h-5 w-5 shrink-0 text-slate-400 transition group-open:rotate-180" />
                </summary>
                <p className="mt-3 leading-7 text-slate-600">{t(a)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final call */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl bg-blue-800 px-6 py-14 text-center text-white sm:px-12">
          <div className="absolute -end-20 -top-20 h-64 w-64 rounded-full bg-blue-600/40 blur-3xl" />
          <h2 className="relative text-3xl font-extrabold tracking-tight sm:text-4xl">{t('Give your business the website it deserves')}</h2>
          <p className="relative mx-auto mt-3 max-w-xl text-blue-100">{t('Start today for free. When you need help, a real team is a message away.')}</p>
          <div className="relative mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/get-started" className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 font-semibold text-blue-900 hover:bg-blue-50">{t('Start free')} <Plus className="h-4 w-4" /></Link>
            {SITE.whatsapp && <a href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-white/40 px-6 py-3.5 font-semibold hover:bg-white/10"><MessageCircle className="h-4 w-4" />{t('Ask us on WhatsApp')}</a>}
            {!SITE.whatsapp && <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl border border-white/40 px-6 py-3.5 font-semibold hover:bg-white/10"><Clock className="h-4 w-4" />{t('Talk to us')}</Link>}
          </div>
        </div>
      </section>
    </SiteLayout>
  )
}
